import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { 
  getAdminReviews, 
  moderateReview, 
  toggleFeatured,
  bulkModerateReviews 
} from '../../../services/reviewService';
import { 
  FiStar, FiFlag, FiEye, FiEyeOff, FiTrash2, FiRefreshCw,
  FiCheck, FiX, FiAlertTriangle, FiSearch, FiFilter
} from 'react-icons/fi';
import ReviewDetailModal from './ReviewDetailModal';

const CommunityReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    flagged: 0,
    hidden: 0,
    featured: 0,
    autoFlagged: 0,
    newToday: 0,
    averageRating: 0,
    mostReviewedProduct: null,
    mostReviewedCategory: null
  });
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Filters
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const filters = { sortBy };
      
      // Apply filter based on active tab
      switch (activeFilter) {
        case 'flagged':
          filters.status = 'flagged';
          break;
        case 'hidden':
          filters.status = 'hidden';
          break;
        case 'featured':
          filters.featured = 'true';
          break;
        case 'auto-flagged':
          filters.autoFlagged = 'true';
          break;
        case 'new-today':
          // This is handled server-side
          break;
        default:
          // 'all' - no filter
          break;
      }
      
      if (searchTerm) {
        filters.search = searchTerm;
      }

      const response = await getAdminReviews(filters);
      setReviews(response.reviews || []);
      setStats(response.stats || stats);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchTerm, sortBy]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleModerate = async (reviewId, action) => {
    try {
      await moderateReview(reviewId, action);
      toast.success(`Review ${action === 'hide' ? 'hidden' : action === 'restore' ? 'restored' : 'deleted'} successfully`);
      fetchReviews();
    } catch (error) {
      console.error('Error moderating review:', error);
      toast.error('Failed to moderate review');
    }
  };

  const handleToggleFeatured = async (reviewId, currentFeatured) => {
    try {
      await toggleFeatured(reviewId, !currentFeatured);
      toast.success(currentFeatured ? 'Review unfeatured' : 'Review featured!');
      fetchReviews();
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Failed to update featured status');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.warning('Please select reviews first');
      return;
    }
    
    if (action === 'delete' && !window.confirm(`Delete ${selectedIds.length} reviews? This cannot be undone.`)) {
      return;
    }

    try {
      await bulkModerateReviews(selectedIds, action);
      toast.success(`${selectedIds.length} reviews ${action}${action === 'hide' ? 'd' : 'd'}`);
      setSelectedIds([]);
      fetchReviews();
    } catch (error) {
      console.error('Error bulk moderating:', error);
      toast.error('Failed to moderate reviews');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === reviews.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(reviews.map(r => r._id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const openReviewDetail = (review) => {
    setSelectedReview(review);
    setShowModal(true);
  };

  const getStatusBadge = (status, isAutoFlagged) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      flagged: 'bg-yellow-100 text-yellow-800',
      hidden: 'bg-gray-100 text-gray-600',
      deleted: 'bg-red-100 text-red-800'
    };
    
    return (
      <div className="flex items-center gap-1">
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badges[status] || badges.active}`}>
          {status}
        </span>
        {isAutoFlagged && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
            auto
          </span>
        )}
      </div>
    );
  };

  const getRepurchaseBadge = (value) => {
    const colors = {
      yes: 'text-green-600',
      no: 'text-red-600',
      maybe: 'text-yellow-600'
    };
    return (
      <span className={`font-medium ${colors[value] || 'text-gray-600'}`}>
        {value === 'yes' ? '✓ Yes' : value === 'no' ? '✗ No' : '~ Maybe'}
      </span>
    );
  };

  const filterTabs = [
    { key: 'all', label: 'All Reviews', count: stats.total },
    { key: 'flagged', label: 'Flagged', count: stats.flagged },
    { key: 'auto-flagged', label: 'Auto-Flagged', count: stats.autoFlagged },
    { key: 'hidden', label: 'Hidden', count: stats.hidden },
    { key: 'featured', label: 'Featured', count: stats.featured },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Community Reviews</h1>
            <p className="text-gray-600">Moderate and manage user reviews</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {stats.newToday > 0 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  {stats.newToday} new today
                </span>
              )}
            </span>
            <button
              onClick={fetchReviews}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              title="Refresh"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Stats Cards - Row 1: Core Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Reviews</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-500">Active</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-yellow-600">{stats.flagged}</div>
            <div className="text-sm text-gray-500">Flagged</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-orange-600">{stats.autoFlagged}</div>
            <div className="text-sm text-gray-500">Auto-Flagged</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-gray-500">{stats.hidden}</div>
            <div className="text-sm text-gray-500">Hidden</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-primary-600">{stats.featured}</div>
            <div className="text-sm text-gray-500">Featured ⭐</div>
          </div>
        </div>

        {/* Stats Cards - Row 2: Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Average Rating */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-amber-500 flex items-center gap-1">
                  {stats.averageRating || 0}
                  <FiStar className="w-5 h-5 fill-current" />
                </div>
                <div className="text-sm text-gray-500">Average Rating</div>
              </div>
              <div className="text-3xl">📊</div>
            </div>
          </div>

          {/* Most Reviewed Product */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                {stats.mostReviewedProduct ? (
                  <>
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {stats.mostReviewedProduct.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {stats.mostReviewedProduct.brand}
                    </div>
                    <div className="text-sm text-primary-600 font-medium mt-1">
                      {stats.mostReviewedProduct.reviewCount} reviews
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-400">No reviews yet</div>
                )}
                <div className="text-xs text-gray-400 mt-1">Most Reviewed Product</div>
              </div>
              <div className="text-3xl ml-2">🏆</div>
            </div>
          </div>

          {/* Most Reviewed Category */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                {stats.mostReviewedCategory ? (
                  <>
                    <div className="text-sm font-semibold text-gray-900 capitalize">
                      {stats.mostReviewedCategory.name}
                    </div>
                    <div className="text-sm text-primary-600 font-medium mt-1">
                      {stats.mostReviewedCategory.reviewCount} reviews
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-400">No reviews yet</div>
                )}
                <div className="text-xs text-gray-400 mt-1">Most Reviewed Category</div>
              </div>
              <div className="text-3xl ml-2">📁</div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 overflow-x-auto pb-px" aria-label="Tabs">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeFilter === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeFilter === tab.key 
                      ? 'bg-primary-100 text-primary-600' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search reviews..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="recent">Most Recent</option>
            <option value="oldest">Oldest First</option>
            <option value="flags">Most Flagged</option>
            <option value="rating-high">Highest Rating</option>
            <option value="rating-low">Lowest Rating</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-3 p-3 bg-primary-50 rounded-lg">
            <span className="text-sm font-medium text-primary-800">
              {selectedIds.length} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction('hide')}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Hide All
              </button>
              <button
                onClick={() => handleBulkAction('restore')}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                Restore All
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete All
              </button>
              <button
                onClick={() => setSelectedIds([])}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Reviews Table */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <FiFilter className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No reviews found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeFilter !== 'all' ? 'Try changing filters' : 'Reviews will appear here when users submit them'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === reviews.length && reviews.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Repurchase
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Review
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Flags
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reviews.map((review) => (
                    <tr 
                      key={review._id} 
                      className={`hover:bg-gray-50 ${review.isFeatured ? 'bg-yellow-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(review._id)}
                          onChange={() => toggleSelect(review._id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[180px]">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {review.product?.name || 'Unknown Product'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {review.product?.brand}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">{review.rating}</span>
                          <FiStar className="ml-1 w-4 h-4 text-yellow-400 fill-current" />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getRepurchaseBadge(review.wouldRepurchase)}
                      </td>
                      <td className="px-4 py-3">
                        <div 
                          className="max-w-[200px] text-sm text-gray-600 truncate cursor-pointer hover:text-primary-600"
                          onClick={() => openReviewDetail(review)}
                          title="Click to view full review"
                        >
                          {review.quickTake || <span className="text-gray-400 italic">No text</span>}
                        </div>
                        {review.nickname && (
                          <div className="text-xs text-gray-400">by {review.nickname}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(review.status, review.isAutoFlagged)}
                      </td>
                      <td className="px-4 py-3">
                        {review.flagCount > 0 ? (
                          <div className="flex items-center text-yellow-600">
                            <FiFlag className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">{review.flagCount}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(review.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Feature toggle */}
                          <button
                            onClick={() => handleToggleFeatured(review._id, review.isFeatured)}
                            className={`p-1.5 rounded ${
                              review.isFeatured 
                                ? 'text-yellow-600 bg-yellow-100 hover:bg-yellow-200' 
                                : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                            }`}
                            title={review.isFeatured ? 'Remove from featured' : 'Mark as featured'}
                          >
                            <FiStar className={review.isFeatured ? 'fill-current' : ''} size={16} />
                          </button>
                          
                          {/* View detail */}
                          <button
                            onClick={() => openReviewDetail(review)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 rounded hover:bg-primary-50"
                            title="View details"
                          >
                            <FiEye size={16} />
                          </button>

                          {/* Hide/Restore */}
                          {review.status === 'active' ? (
                            <button
                              onClick={() => handleModerate(review._id, 'hide')}
                              className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                              title="Hide review"
                            >
                              <FiEyeOff size={16} />
                            </button>
                          ) : review.status !== 'deleted' ? (
                            <button
                              onClick={() => handleModerate(review._id, 'restore')}
                              className="p-1.5 text-green-500 hover:text-green-700 rounded hover:bg-green-50"
                              title="Restore review"
                            >
                              <FiRefreshCw size={16} />
                            </button>
                          ) : null}

                          {/* Delete */}
                          {review.status !== 'deleted' && (
                            <button
                              onClick={() => {
                                if (window.confirm('Delete this review permanently?')) {
                                  handleModerate(review._id, 'delete');
                                }
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                              title="Delete review"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination info */}
        {reviews.length > 0 && (
          <div className="text-sm text-gray-500 text-center">
            Showing {reviews.length} of {stats.total} reviews
          </div>
        )}
      </div>

      {/* Review Detail Modal */}
      {showModal && selectedReview && (
        <ReviewDetailModal
          review={selectedReview}
          onClose={() => {
            setShowModal(false);
            setSelectedReview(null);
          }}
          onModerate={(action) => {
            handleModerate(selectedReview._id, action);
            setShowModal(false);
            setSelectedReview(null);
          }}
          onToggleFeatured={() => {
            handleToggleFeatured(selectedReview._id, selectedReview.isFeatured);
            setShowModal(false);
            setSelectedReview(null);
          }}
        />
      )}
    </AdminLayout>
  );
};

export default CommunityReviewsPage;

